<script lang="ts" setup>
import { computed } from 'vue';
import { useHead } from '#app/composables/head';
import { useRoute } from '#app/composables/router';
import { emptyBlockSlot } from '#layers/block-engine/public';
import { CREATE_MODE_ITEM_ID, parseItemPrimaryKeyFromRouteString, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { SpacePageRouteMarker, usePageActions } from '#layers/spaces/public';
import PageShell from '#layers/ui-workspace/components/PageShell/PageShell.vue';

const { id } = defineProps<{
	id: string;
}>();

const store = useWorkspaceEntityStore();
const { data: page } = await store.MonospacePage.query((q) => q.first({
	key: id,
	fetchPolicy: 'cache-and-fetch',
}));

await store.MonospaceBlock.query((q) => q.many({
	fetchPolicy: 'cache-and-fetch',
}));

useHead(() => ({
	title: page.value?.title,
}));

const { editPage } = usePageActions();
const route = useRoute();
const { findCollectionById } = useWorkspaceSchemaStore();

const pageCollection = computed(() => page.value?.collectionId ? findCollectionById(page.value.collectionId) : undefined);
const pageItemId = computed(() => {
	const routeItemId = Array.isArray(route.params.itemId) ? route.params.itemId[0] : route.params.itemId;

	if (page.value?.type !== 'detail' || !routeItemId || routeItemId === CREATE_MODE_ITEM_ID || !pageCollection.value) {
		return;
	}

	return parseItemPrimaryKeyFromRouteString(routeItemId, pageCollection.value);
});

const context = computed(() => ({
	pageId: page.value?.id,
	pageType: page.value?.type,
	pageIntent: page.value?.intent,
	collectionId: page.value?.collectionId,
	pageIsFullWidth: page.value?.isFullWidth,
	itemId: pageItemId.value,
}));

/**
 * Detect if the current route is exactly the page route (not a child route)
 */
const isSpacePageLeaf = computed(() => {
	const matched = route.matched.filter((m) => m.meta[SpacePageRouteMarker]);
	return matched.at(-1)?.name === id;
});
</script>

<template>
	<PageShell v-if="isSpacePageLeaf" :context>
		<BlockRender
			v-if="page?.block"
			:block="page.block.id"
			:context
			class="h-full"
		>
			<template #[emptyBlockSlot]>
				<DEmpty
					fill
					action-button-primary
					:title="$t('page.block_empty')"
					:description="$t('page.block_empty_description')"
					:action-button-label="$t('page.start_building')"
					action-button-icon="lucide:wrench"
					icon="lucide:box"
					@action="editPage(page)"
				/>
			</template>
		</BlockRender>
	</PageShell>

	<!-- Display the child route instead -->
	<NuxtPage v-else />
</template>
